import { assertNever, CommentReport, FullSurveyReport, segmentListsMatch, SelectionReport, SurveyAnswer, SurveyData, SurveyQuestion, ScoreValueMapping, SurveyScore, QuestionSummary } from '..';
import { DefaultScoreValueMapping, DefaultSurveyQuestionOptions } from '../DTOs/survey/SurveyQuestion';
import { SelectionSummaryWrapper } from '../DTOs/survey/SurveySelectionSummary';

export const surveyReportHelper =
{
    addAnswer,
    getEmptyDefaultQuestion,
    getDefaultScoreMapping,
};

function addAnswer(survey:SurveyData, report:FullSurveyReport, answer:SurveyAnswer){
    const selectionReport = getOrCreateSelectionReport(survey, report, answer);
    selectionReport.answerCount++;
    
    for(let i=0; i<selectionReport.questionIndexedSummaries.length; i++){
        const selection = answer.selections[i];
        
        const reportSummary = selectionReport.questionIndexedSummaries[i];

        for(const value of selection.values){
            const option = reportSummary.selectionSummary.optionValueCounts.find(c => c.value === value);
            if(!option)
                throw new Error('Value selected in answer does not exist in the expected survey report value options:' + value);

            option.count++;
        }

        if(answer.selections[i].comment !== '')
            selectionReport.questionIndexedSummaries[i].commentCount++;
    }

    const commentReport = getOrCreateCommentReport(survey, report, answer);
    for(let i=0; i<commentReport.questionIndexedComments.length; i++){
        if(answer.selections[i].comment !== ''){
            commentReport.questionIndexedComments[i].comments.push({
                text:answer.selections[i].comment,
                selectionValue:answer.selections[i].values[0] || ''
            });
        }
    }
}

function getDefaultScoreMapping(invert:boolean): ScoreValueMapping[]
{
    if(invert)
        return DefaultScoreValueMapping.map(sm => ({ value:sm.value, score:6-sm.score as SurveyScore }));
    else
        return DefaultScoreValueMapping.map(sm => ({ value:sm.value, score:sm.score }));
}

function getOrCreateSelectionReport(survey:SurveyData, report:FullSurveyReport, answer:SurveyAnswer):SelectionReport{

    let existingReport = report.selectionReport.segmentReports.find(cs => 
        segmentListsMatch(cs.segments, answer.segments));
    
    if(!existingReport){
        existingReport = {
            segments:answer.segments,
            answerCount: 0,
            questionIndexedSummaries: 
                survey.themes.flatMap(t =>
                    t.questions.map(q => {
                        const summary:QuestionSummary = {
                            selectionSummary: SelectionSummaryWrapper.createFromQuestion(q).data,
                            commentCount:0
                        };

                        if(q.type === 'default'){
                            summary.selectionSummary.optionValueCounts = DefaultSurveyQuestionOptions.map(o => ({ value:o.value, count:0 }));
                        }

                        else if(q.type === 'custom'){
                            summary.selectionSummary.optionValueCounts = q.options.map(o => ({ value:o.value, count:0 }));
                        }

                        else if(q.type === 'multiple-choice'){
                            summary.selectionSummary.optionValueCounts = q.options.map(o => ({ value:o.value, count:0 }));
                        }

                        else {
                            assertNever(q);
                            throw new Error('Unsupported question type');
                        }

                        return summary;
                    })
                )
        };
        report.selectionReport.segmentReports.push(existingReport);
    }
    return existingReport;
}

function getOrCreateCommentReport(survey:SurveyData, report:FullSurveyReport, answer:SurveyAnswer):CommentReport{

    let existingReport = report.commentReport.segmentReports.find(cs => 
        segmentListsMatch(cs.segments, answer.segments));
    
    if(!existingReport){
        existingReport = {
            segments:answer.segments,
            questionIndexedComments:
                survey.themes.flatMap(t =>
                    t.questions.map(() => {
                        return {
                            comments:[]
                        };
                    })
                )
        };
        report.commentReport.segmentReports.push(existingReport);
    }
    return existingReport;
}

function getEmptyDefaultQuestion():SurveyQuestion
{
    return {
        title:'',
        canComment:true,
        indicators:[],
        translations:[],
        type:'default',
        invertScore:false
    };
}